import {
  ChatActionCodec,
  ChatActionType,
  ChatAttachmentType,
  IChatAction,
  IChatImageAttachment,
  IChatPdfAttachment,
} from '@engoo/realtime-protocol/chat';
import {
  IPersistedAction,
  PersistedActionCodec,
  StreamPosition,
  makeStreamPosition,
} from '@engoo/realtime-protocol/persisted';
import { IPoint } from '@engoo/realtime-protocol/point';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { OrderedMap, Record } from 'immutable';
import { makeTimestamp } from '@engoo/realtime-protocol/util/timeSchemas';

export interface IChatImage extends IChatImageAttachment {
  dimensions?: ChatImageDimensions;
}

export class ChatImage extends Record<IChatImage>({
  dimensions: undefined,
  type:       ChatAttachmentType.Image,
  url:        '',
}) {
  public constructor({ dimensions, ...props }: IChatImageAttachment) {
    super({
      dimensions: parseDimensions(dimensions),
      ...props,
    });
  }
}

export class ChatImageDimensions extends Record<IPoint>({ x: 0, y: 0 }) {
  // Override constructor to require all fields instead of accepting a Partial.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: IPoint) {
    super(props);
  }
}

const parseDimensions = (dimensions: IPoint | undefined) =>
  dimensions && !(dimensions instanceof ChatImageDimensions)
  ? new ChatImageDimensions(dimensions)
  : dimensions;

interface IChatPdf extends IChatPdfAttachment {
  previewDimensions: ChatImageDimensions;
}

export class ChatPdf extends Record<IChatPdf>({
  previewDimensions: new ChatImageDimensions({ x: 0, y: 0 }),
  previewUrl:        '',
  name:              '',
  type:              ChatAttachmentType.PDF,
  url:               '',
}) {
  public constructor({ previewDimensions, ...props }: IChatPdfAttachment) {
    super({
      previewDimensions: parseDimensions(previewDimensions),
      ...props,
    });
  }
}

export type ChatAttachment = ChatImage | ChatPdf;

interface IChatMessageBase<T extends boolean> {
  attachment?: ChatAttachment;
  contents?: string;
  deleted: boolean;
  edited: boolean;
  error?: string;
  pending: T;
  position: StreamPosition;
  // TODO: Perhaps this type should be discriminated based on the type?
  type: ChatActionType.Message | ChatActionType.Image;
}

interface IPendingChatMessage extends IChatMessageBase<true> {}

// Structure for pending actions.
export class PendingChatMessage extends Record<IPendingChatMessage>({
  attachment: undefined,
  contents:   undefined,
  deleted:    false,
  edited:     false,
  error:      undefined,
  pending:    true,
  position:   makeStreamPosition(0),
  type:       ChatActionType.Message,
}) {
  // Override constructor to require all fields instead of accepting a Partial.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: IPendingChatMessage) {
    super(props);
  }
}

interface IPersistedChatMessage extends IChatMessageBase<false>, IPersistedAction {}

// Structure for remote messages.
export class PersistedChatMessage extends Record<IPersistedChatMessage>({
  attachment: undefined,
  contents:   undefined,
  deleted:    false,
  edited:     false,
  error:      undefined,
  pending:    false,
  position:   makeStreamPosition(0),
  timestamp:  makeTimestamp(0),
  type:       ChatActionType.Message,
  user:       undefined,
}) {
  // Override constructor to require all fields instead of accepting a Partial.
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: IPersistedChatMessage) {
    super(props);
  }
}

export type ChatMessage = PendingChatMessage | PersistedChatMessage;

// Handles setting non-shared attributes.
const updateChatMessageContents = (chatAction: IChatAction) => {
  return <P extends IChatMessageBase<boolean>>(chatMessage: Record<P>): Record<P> => {
    switch (chatAction.type) {
      case ChatActionType.Message:
        return chatMessage.set('contents', chatAction.contents);

      case ChatActionType.Edit:
        return chatMessage
          .set('contents', chatAction.contents)
          .set('edited', true);

      case ChatActionType.Delete:
        return chatMessage
          .set('attachment', undefined)
          .set('contents', undefined)
          .set('deleted', true);

      case ChatActionType.Image:
      case ChatActionType.Attachment: {
        const { attachment } = chatAction;

        return chatMessage.withMutations((message) => {
          if (chatAction.type === ChatActionType.Image) {
            message.set('error', chatAction.error);
          }

          if (attachment) {
            switch (attachment.type) {
              case ChatAttachmentType.Image:
                message.set('attachment', new ChatImage(attachment));
                break;
              case ChatAttachmentType.PDF:
                message.set('attachment', new ChatPdf(attachment));
                break;
            }
          }
        });
      }

      case ChatActionType.Error:
        return chatMessage.set('error', chatAction.error);
    }

    return chatMessage;
  };
};

export type PendingChatMessages = OrderedMap<number, PendingChatMessage>;
export type PersistedChatMessages = OrderedMap<number, PersistedChatMessage>;

export class ChatState extends Record({
  pending: OrderedMap<number, PendingChatMessage>(),
  persisted: OrderedMap<number, PersistedChatMessage>(),
}) {}

export const chatReducer = (state = new ChatState(), action?: unknown): ChatState => {
  // Only handle valid chat actions.
  return pipe(
    ChatActionCodec.decode(action),
    fold(
      () => {
        return state;
      },
      (chatAction) => {
        // Shared attributes for instantiating persisted and pending.
        const sharedAttributes = {
          deleted: false,
          edited: false,
        };

        const applyChatAction = updateChatMessageContents(chatAction);

        return pipe(
          PersistedActionCodec.decode(chatAction),
          fold(
            // Handle non persisted actions.
            () => {
              switch (chatAction.type) {
                case ChatActionType.Image:
                case ChatActionType.Message: {
                  const lastPersisted = state.persisted.last();
                  const pendingChatMessage = new PendingChatMessage({
                    ...sharedAttributes,
                    pending: true,
                    // Use position of last item on persisted list so that message is displayed at end of
                    // messages while waiting to get real position from server.
                    position: makeStreamPosition(lastPersisted ? lastPersisted.position + 1 : 0),
                    type: chatAction.type,
                  });

                  return state.update('pending', (pending) => {
                    return pending.set(state.pending.size, applyChatAction(pendingChatMessage));
                  });
                }
              }

              return state;
            },
            // Handle persisted actions.
            (persistedAction) => {
              switch (chatAction.type) {
                case ChatActionType.Image:
                case ChatActionType.Message: {
                  const { position } = persistedAction;
                  const persistedChatMessage = new PersistedChatMessage({
                    ...sharedAttributes,
                    ...persistedAction,
                    pending: false,
                    type: chatAction.type,
                  });

                  return state.update('persisted', (persisted) => {
                    return persisted.set(position, applyChatAction(persistedChatMessage));
                  });
                }

                case ChatActionType.Edit:
                case ChatActionType.Delete:
                case ChatActionType.Attachment:
                case ChatActionType.Error: {
                  const { message_id } = chatAction;

                  if (state.persisted.has(message_id)) {
                    // The latest persisted action will have the id of the user who most recently operated on a message
                    const { user: actionUserId } = persistedAction;

                    // Since the id of the user is only set in state when the first sending the message
                    // getting the message from state and checking the user will yield the message author's id
                    const targetPersistedMessage = state.persisted.get(message_id);

                    // Check that the id of the message author matches the id of the user editing the message
                    if (targetPersistedMessage && targetPersistedMessage.user !== actionUserId) {
                      return state;
                    }

                    return state.update('persisted', (persisted) => {
                      return persisted.update(message_id, applyChatAction);
                    });
                  }

                  break;
                }
              }

              return state;
            },
          ),
        );
      },
    ),
  );
};
